import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import Header from "../components/Header";
import { Button, Col, Row, Form, Alert, Spinner } from "react-bootstrap";
import { FiPlus } from "react-icons/fi";
import { getHost } from "../utils/utils";

const ADD_MONITOR_API = `${getHost()}/api/monitors`;
const getToken = () => localStorage.getItem("jwt");

const AddMonitor = () => {
  const { toggleSidebar } = useOutletContext();
  const DEFAULT_SERVER_IP = "152.53.147.58";
  const [monitor, setMonitor] = useState({
    name: "",
    folder_name: "",
    server_ips: DEFAULT_SERVER_IP,
    category: "Sneakers",
    has_regions: false,
    region: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field, value) => {
    setMonitor((m) => ({ ...m, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    // Parse server IPs (comma-separated)
    const serverIpsArray = monitor.server_ips
      .split(",")
      .map((ip) => ip.trim())
      .filter((ip) => ip);

    if (serverIpsArray.length === 0) {
      setError("Please enter at least one server IP");
      setLoading(false);
      return;
    }

    const payload = {
      name: monitor.name,
      folder_name: monitor.folder_name,
      server_ips: serverIpsArray,
      category: monitor.category,
      has_regions: monitor.has_regions,
      region: monitor.region || null,
    };

    fetch(ADD_MONITOR_API, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || "Failed to create monitor");
        }
        return res.json();
      })
      .then(() => {
        setSuccess(true);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Could not create monitor.");
        setLoading(false);
      });
  };

  return (
    <div className="w-100 dashboard-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="px-4 pb-3 content-body">
        {/* Header */}
        <div className="mb-3">
          <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
            <FiPlus className="text-white" />
          </div>
          <h2 className="text-main fs-20 fw-600 mb-1">Add New Monitor</h2>
          <p className="text-white-65 fs-14 mb-0">
            Deploy a new monitor to the system
          </p>
        </div>

        <Form onSubmit={handleSubmit}>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" className="mb-3">
              Monitor created successfully!
            </Alert>
          )}

          {/* Monitor Name */}
          <Row className="align-items-center mb-3">
            <Col md={7}>
              <Form.Control
                type="text"
                className="form-input-field border rounded py-2 px-3"
                placeholder="e.g., Nike SNKRS"
                value={monitor.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
              />
            </Col>
            <Col
              md={5}
              className="d-flex align-items-center justify-content-end"
            >
              <div className="text-end">
                <div className="text-main fs-13 fw-500 mb-1">Monitor Name</div>
                <div className="text-white-45 fs-11">Display name</div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <hr className="border-secondary opacity-25 my-3" />
            </Col>
          </Row>

          {/* Folder Name */}
          <Row className="align-items-center mb-3">
            <Col md={7}>
              <Form.Control
                type="text"
                className="form-input-field border rounded py-2 px-3"
                placeholder="e.g., nike-snkrs"
                value={monitor.folder_name}
                onChange={(e) => handleChange("folder_name", e.target.value)}
                required
              />
            </Col>
            <Col
              md={5}
              className="d-flex align-items-center justify-content-end"
            >
              <div className="text-end">
                <div className="text-main fs-13 fw-500 mb-1">Folder Name</div>
                <div className="text-white-45 fs-11">
                  Server folder identifier
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <hr className="border-secondary opacity-25 my-3" />
            </Col>
          </Row>

          {/* Server IPs */}
          <Row className="align-items-center mb-3">
            <Col md={7}>
              <Form.Control
                type="text"
                className="form-input-field border rounded py-2 px-3"
                placeholder="e.g., 192.168.1.1, 192.168.1.2"
                value={monitor.server_ips}
                onChange={(e) => handleChange("server_ips", e.target.value)}
                required
              />
            </Col>
            <Col
              md={5}
              className="d-flex align-items-center justify-content-end"
            >
              <div className="text-end">
                <div className="text-main fs-13 fw-500 mb-1">Server IPs</div>
                <div className="text-white-45 fs-11">Comma-separated list</div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <hr className="border-secondary opacity-25 my-3" />
            </Col>
          </Row>

          {/* Category */}
          <Row className="align-items-center mb-3">
            <Col md={7}>
              <Form.Select
                value={monitor.category}
                onChange={(e) => handleChange("category", e.target.value)}
                className="form-input-field rounded-3 text-white-65 custom-select-white py-2 px-3"
              >
                <option value="Sneakers">Sneakers</option>
                <option value="Alternative">Alternative</option>
                <option value="Ticket">Ticket</option>
                <option value="TCG">TCG</option>
              </Form.Select>
            </Col>
            <Col
              md={5}
              className="d-flex align-items-center justify-content-end"
            >
              <div className="text-end">
                <div className="text-main fs-13 fw-500 mb-1">Category</div>
                <div className="text-white-45 fs-11">Monitor type</div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <hr className="border-secondary opacity-25 my-3" />
            </Col>
          </Row>

          {/* Region */}
          <Row className="align-items-center mb-3">
            <Col md={7}>
              <Form.Select
                value={monitor.region}
                onChange={(e) => handleChange("region", e.target.value)}
                className="form-input-field rounded-3 text-white-65 custom-select-white py-2 px-3"
              >
                <option value="">None</option>
                <option value="INT">INT (International)</option>
                <option value="FR">FR (France)</option>
                <option value="DE">DE (Germany)</option>
                <option value="ES">ES (Spain)</option>
                <option value="IT">IT (Italy)</option>
                <option value="NL">NL (Netherlands)</option>
                <option value="BE">BE (Belgium)</option>
                <option value="AT">AT (Austria)</option>
                <option value="PT">PT (Portugal)</option>
                <option value="PL">PL (Poland)</option>
                <option value="CZ">CZ (Czech Republic)</option>
                <option value="UK">UK (United Kingdom)</option>
                <option value="US">US (United States)</option>
                <option value="CH">CH (Switzerland)</option>
              </Form.Select>
            </Col>
            <Col
              md={5}
              className="d-flex align-items-center justify-content-end"
            >
              <div className="text-end">
                <div className="text-main fs-13 fw-500 mb-1">Region</div>
                <div className="text-white-45 fs-11">Country or market</div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <hr className="border-secondary opacity-25 my-3" />
            </Col>
          </Row>

          <Row className="align-items-center mb-3">
            <Col md={7}>
              <Form.Check
                type="checkbox"
                label="Monitor has regions support"
                checked={monitor.has_regions}
                onChange={(e) => handleChange("has_regions", e.target.checked)}
                className="text-white-65"
              />
            </Col>
            <Col
              md={5}
              className="d-flex align-items-center justify-content-end"
            >
              <div className="text-end">
                <div className="text-main fs-13 fw-500 mb-1">Has Regions</div>
                <div className="text-white-45 fs-11">
                  Supports regional webhooks
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <hr className="border-secondary opacity-25 my-3" />
            </Col>
          </Row>

          {/* Submit Button */}
          <Row>
            <Col md={7}>
              <div className="mt-4">
                <Button
                  type="submit"
                  className="btn-primary rounded-3 py-2 px-4 fw-600 fs-14"
                  disabled={loading}
                  style={{
                    backgroundColor: "#4A4A4A",
                    borderColor: "#4A4A4A",
                    color: "white",
                  }}
                >
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Create Monitor
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default AddMonitor;
